import React, { useId, useState } from 'react';
import {
    Calendar,
    Car,
    Fuel,
    Settings,
    Zap,
    Shield,
    Scale,
    Activity,
    Eye,
    Hash,
} from 'lucide-react';
import type { Vehicle } from '../../types/vinvrmsearch.types';
import { Badge, Button, Card, CardTitle } from '@shared/components';
import { MotHistoryPanel } from '@/features/mot-history';

const MOT_TONE_CLASSES = {
    gray: {
        bg: 'bg-mp-background border-mp-border',
        divider: 'border-mp-border',
        icon: 'text-mp-muted',
        text: 'text-mp-foreground',
        value: 'text-mp-muted',
    },
    red: {
        bg: 'bg-mp-error-content border-mp-error/20',
        divider: 'border-mp-error/20',
        icon: 'text-mp-error',
        text: 'text-mp-error',
        value: 'text-mp-error',
    },
    green: {
        bg: 'bg-mp-success-content border-mp-success/20',
        divider: 'border-mp-success/20',
        icon: 'text-mp-success',
        text: 'text-mp-success',
        value: 'text-mp-success',
    },
};

interface VehicleCardProps {
    vehicle: Vehicle;
    onSelect?: (vehicle: Vehicle) => void;
    showDetailedInfo?: boolean;
    showMotHistory?: boolean;
}

export const VehicleCard: React.FC<VehicleCardProps> = ({
    vehicle,
    onSelect,
    showDetailedInfo = false,
    showMotHistory = false,
}) => {
    const [isMotHistoryOpen, setIsMotHistoryOpen] = useState(false);
    const motPanelId = useId();
    const motVrm = vehicle.current_status?.vrm ?? undefined;
    const canViewMotHistory = showMotHistory && Boolean(vehicle.vin || motVrm);

    const formatDate = (dateString?: string) => {
        if (!dateString) {
            return 'Unknown';
        }

        try {
            return new Date(dateString).toLocaleDateString('en-UK');
        } catch {
            return dateString;
        }
    };

    const hasMotInfo = Boolean(vehicle.last_mot_date || vehicle.mot_expiry_date);
    const showMotPanel = hasMotInfo || canViewMotHistory;

    return (
        <Card className="relative flex flex-col md:col-span-2 2xl:col-span-3">
            {/* Card Body */}
            <div className="p-4 space-y-4">
                {/* Title and Primary Info */}
                <div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="secondary">
                            {vehicle.derivative?.short_name || vehicle.full_description}
                        </Badge>
                        <Badge variant="primary">{vehicle.colour}</Badge>
                        <Badge variant="outline">
                            {vehicle.doors} door{vehicle.doors !== 1 ? 's' : ''}
                        </Badge>
                        {vehicle.is_imported && <Badge variant="info">Imported</Badge>}
                    </div>
                    {vehicle.derivative?.make?.name && vehicle.derivative?.model?.name && (
                        <CardTitle>
                            {vehicle.derivative.make.name} {vehicle.derivative.model.name}
                        </CardTitle>
                    )}
                </div>

                <div
                    className={`grid grid-cols-1 gap-3 mb-3 ${showMotPanel ? 'sm:grid-cols-2' : ''}`}
                >
                    <div className="bg-mp-info-content rounded-lg border border-mp-info/20">
                        <div className="flex items-center gap-2 px-4 py-2 border-b border-mp-info/20">
                            <Hash className="w-4 h-4 text-mp-info" />
                            <span className="text-sm font-medium text-mp-info">
                                Vehicle Identification
                            </span>
                        </div>
                        <div className="p-4 text-sm text-mp-info space-y-1">
                            <div>
                                Registration:{' '}
                                <span className="font-semibold">
                                    {vehicle.current_status?.formatted_vrm}
                                </span>
                            </div>
                            <div>
                                VIN: <span className="font-semibold">{vehicle.vin}</span>
                            </div>
                            <div>
                                First Registered:{' '}
                                <span className="font-semibold">
                                    {formatDate(vehicle.date_of_first_registration)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* MOT Information */}
                    {showMotPanel &&
                        (() => {
                            const isMotExpired =
                                vehicle.mot_expiry_date &&
                                new Date(vehicle.mot_expiry_date) < new Date();
                            const tone = !hasMotInfo ? 'gray' : isMotExpired ? 'red' : 'green';
                            const {
                                bg: bgClass,
                                divider: dividerClass,
                                icon: iconClass,
                                text: textClass,
                                value: valueClass,
                            } = MOT_TONE_CLASSES[tone];

                            return (
                                <div className={`flex flex-col rounded-lg border ${bgClass}`}>
                                    <div
                                        className={`flex items-center gap-2 px-4 py-2 border-b ${dividerClass}`}
                                    >
                                        <Shield className={`w-4 h-4 ${iconClass}`} />
                                        <span className={`text-sm font-medium ${textClass}`}>
                                            MOT Status
                                        </span>
                                    </div>
                                    <div className="flex flex-col grow p-4">
                                        <div className={`text-sm ${valueClass} space-y-1`}>
                                            {!hasMotInfo && <div>No MOT dates on record</div>}
                                            {vehicle.last_mot_date && (
                                                <div>
                                                    Last MOT: {formatDate(vehicle.last_mot_date)}
                                                </div>
                                            )}
                                            {vehicle.mot_expiry_date && (
                                                <div>
                                                    Expires:{' '}
                                                    <span className="font-semibold">
                                                        {formatDate(vehicle.mot_expiry_date)}
                                                    </span>
                                                    {isMotExpired && (
                                                        <span className="font-semibold">
                                                            {' '}
                                                            (Expired)
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {canViewMotHistory && (
                                            <div className="flex grow justify-end items-end">
                                                <Button
                                                    size="none"
                                                    variant="clear"
                                                    className="text-sm text-mp-link"
                                                    ariaExpanded={isMotHistoryOpen}
                                                    ariaControls={motPanelId}
                                                    onClick={() =>
                                                        setIsMotHistoryOpen((open) => !open)
                                                    }
                                                >
                                                    {isMotHistoryOpen
                                                        ? 'Hide MOT history'
                                                        : 'View MOT history'}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                </div>

                {/* Model Details */}
                {vehicle.derivative && (
                    <div className="mb-3 bg-mp-background rounded-lg border border-mp-border">
                        <div className="flex items-center gap-2 px-4 py-2 border-b border-mp-border">
                            <Car className="w-4 h-4 text-mp-muted" />
                            <span className="text-sm font-medium text-mp-foreground">
                                Model Details
                            </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 p-4 text-sm text-mp-muted">
                            {vehicle.derivative.first_year_of_manufacture && (
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 shrink-0 text-mp-muted" />
                                    <span>
                                        First Produced:{' '}
                                        <span className="font-semibold">
                                            {vehicle.derivative.first_year_of_manufacture}
                                        </span>
                                    </span>
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 shrink-0 text-mp-muted" />
                                <span>
                                    Engine Size:{' '}
                                    <span className="font-semibold">
                                        {vehicle.derivative.engine_size?.description ||
                                            vehicle.derivative.engine_size?.size ||
                                            'N/A'}
                                    </span>
                                </span>
                            </div>

                            <div className="flex items-center gap-2">
                                <Settings className="w-4 h-4 shrink-0 text-mp-muted" />
                                <span>
                                    Transmission:{' '}
                                    <span className="font-semibold">
                                        {vehicle.derivative.transmission?.description ||
                                            vehicle.derivative.transmission?.type ||
                                            'N/A'}
                                    </span>
                                </span>
                            </div>

                            <div className="flex items-center gap-2">
                                <Fuel className="w-4 h-4 shrink-0 text-mp-muted" />
                                <span>
                                    Fuel Type:{' '}
                                    <span className="font-semibold">
                                        {vehicle.derivative.fuel_type?.description ||
                                            vehicle.derivative.fuel_type?.name ||
                                            'N/A'}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Detailed Vehicle Specs (if enabled) */}
                {showDetailedInfo && (
                    <div className="space-y-3 mb-4">
                        {/* Engine Power and Performance */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-mp-muted" />
                                <span className="text-mp-foreground">
                                    {vehicle.engine_power_hp} HP
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-mp-muted" />
                                <span className="text-mp-foreground">
                                    {vehicle.engine_power_kw} kW
                                </span>
                            </div>
                        </div>

                        {/* Weight and Emissions */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2">
                                <Scale className="w-4 h-4 text-mp-muted" />
                                <span className="text-mp-foreground">{vehicle.kerb_weight}kg</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Eye className="w-4 h-4 text-mp-muted" />
                                <span className="text-mp-foreground">
                                    {vehicle.co2_emissions}g CO₂
                                </span>
                            </div>
                        </div>

                        {/* Insurance and Fuel Capacity */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-mp-muted" />
                                <span className="text-mp-foreground">
                                    Group {vehicle.insurance_group}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Fuel className="w-4 h-4 text-mp-muted" />
                                <span className="text-mp-foreground">
                                    {vehicle.fuel_tank_capacity}L
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Action */}
                {onSelect && (
                    <div className="flex justify-end items-end mt-auto">
                        <Button size={'sm'} onClick={() => onSelect(vehicle)}>
                            Select Vehicle
                        </Button>
                    </div>
                )}

                {canViewMotHistory && (
                    <div id={motPanelId} className="mt-4" hidden={!isMotHistoryOpen}>
                        {isMotHistoryOpen && (
                            <MotHistoryPanel vin={vehicle.vin} vrm={motVrm} showHeader={false} />
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};
